// Agrégation factuelle des réponses de suivi.
//
// Ce module ne classe pas les réponses et ne déduit aucun niveau, signal ou
// action. Il expose uniquement les valeurs reçues, leurs décomptes et, quand
// il existe, le mode statistique. Une égalité est toujours rendue explicite.

const AXES = ['fatigue', 'digestion', 'faim', 'energie'];

const AXIS_VALUE_LABELS = {
  fatigue: { better: 'Mieux', same: 'Pareil', worse: 'Pire' },
  digestion: { better: 'Mieux', same: 'Pareil', worse: 'Pire' },
  faim: { low: 'Faible', ok: 'OK', high: 'Élevée' },
  energie: { low: 'Basse', ok: 'OK', good: 'Bonne' },
};

const AXIS_LABELS = {
  fatigue: 'Fatigue',
  digestion: 'Digestion',
  faim: 'Faim',
  energie: 'Énergie',
};

function isRecordedValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function orderedValues(axis, counts) {
  const known = Object.keys(AXIS_VALUE_LABELS[axis] || {});
  const unexpected = Object.keys(counts)
    .filter((value) => !known.includes(value))
    .sort((a, b) => a.localeCompare(b, 'fr'));
  return [...known.filter((value) => counts[value]), ...unexpected];
}

function valueLabel(axis, value) {
  return AXIS_VALUE_LABELS[axis]?.[value] || value;
}

function buildAxisSummary(axis, total, valueCounts, dominantValues) {
  const label = AXIS_LABELS[axis] || axis;
  if (total === 0) return `${label} : aucune réponse.`;

  const detail = valueCounts
    .map(({ label: answer, count }) => `${answer} : ${count}`)
    .join(' · ');
  if (dominantValues.length === 1) {
    const mode = valueCounts.find(({ value }) => value === dominantValues[0]);
    return `${label} : ${total} réponse${total > 1 ? 's' : ''} (${detail}). Valeur la plus fréquente : ${mode.label} (${mode.count}/${total}).`;
  }

  const tiedLabels = dominantValues.map((value) => valueLabel(axis, value)).join(', ');
  return `${label} : ${total} réponse${total > 1 ? 's' : ''} (${detail}). Égalité entre les valeurs les plus fréquentes : ${tiedLabels}.`;
}

export function computeAxisTrend(axis, feedbacks = []) {
  const values = (Array.isArray(feedbacks) ? feedbacks : [])
    .map((feedback) => feedback?.[axis])
    .filter(isRecordedValue)
    .map(String);
  const counts = values.reduce((result, value) => {
    result[value] = (result[value] || 0) + 1;
    return result;
  }, {});
  const total = values.length;
  const valueCounts = orderedValues(axis, counts).map((value) => ({
    value,
    label: valueLabel(axis, value),
    count: counts[value],
  }));
  const highestCount = valueCounts.reduce((max, item) => Math.max(max, item.count), 0);
  const dominantValues = highestCount === 0
    ? []
    : valueCounts.filter((item) => item.count === highestCount).map((item) => item.value);
  const dominantValue = dominantValues.length === 1 ? dominantValues[0] : null;

  return {
    axis,
    label: AXIS_LABELS[axis] || axis,
    total,
    counts,
    valueCounts,
    dominantValue,
    dominantLabel: dominantValue ? valueLabel(axis, dominantValue) : null,
    dominantCount: dominantValue ? counts[dominantValue] : 0,
    tiedDominantValues: dominantValues.length > 1 ? dominantValues : [],
    summary: buildAxisSummary(axis, total, valueCounts, dominantValues),
  };
}

export function computeFeedbackTrends(feedbacks = []) {
  const rows = Array.isArray(feedbacks) ? feedbacks : [];
  const sortedFeedbacks = [...rows].sort((a, b) => {
    const da = new Date(a?.date || a?.created_at || 0).getTime();
    const db = new Date(b?.date || b?.created_at || 0).getTime();
    return da - db;
  });

  return {
    dataPoints: sortedFeedbacks.length,
    axisTrends: AXES.map((axis) => computeAxisTrend(axis, sortedFeedbacks)),
  };
}

export function compareAxisTrend(currentTrend, previousTrend) {
  const currentTotal = currentTrend?.total || 0;
  const previousTotal = previousTrend?.total || 0;
  const values = new Set([
    ...Object.keys(currentTrend?.counts || {}),
    ...Object.keys(previousTrend?.counts || {}),
  ]);
  const valueComparisons = [...values]
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((value) => {
      const currentCount = currentTrend?.counts?.[value] || 0;
      const previousCount = previousTrend?.counts?.[value] || 0;
      return {
        value,
        label: valueLabel(currentTrend?.axis || previousTrend?.axis, value),
        currentCount,
        previousCount,
        difference: currentCount - previousCount,
      };
    });

  return {
    hasPrevious: previousTotal > 0,
    currentTotal,
    previousTotal,
    valueComparisons,
  };
}

export { AXIS_LABELS, AXIS_VALUE_LABELS };
